/* eslint-disable prettier/prettier */
import { ChevronDown } from 'lucide-react';
import React, { useState, useRef, useEffect, useMemo } from 'react';

export type Option = {
  label: string;
  value: string;
};

export type SelectProps = {
  data: Option[];
  value?: string;
  onChange: (value: string) => void;
  placeholder?: string;
  className?: string;
  error?: boolean;
  errorMessage?: string;
  disabled?: boolean;
  openScroll?: 'top' | 'selected' | 'active';
};

export const CustomSelect: React.FC<SelectProps> = ({
  data,
  value,
  onChange,
  placeholder = 'Select an option',
  className = '',
  error = false,
  errorMessage = '',
  disabled,
  openScroll = 'top',
}) => {
  const hasOptions = data && data.length > 0;

  const [isOpen, setIsOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState<number>(() => {
    if (!hasOptions) return -1;
    const idx = data.findIndex((d) => d.value === value);
    return idx >= 0 ? idx : 0;
  });

  const dropdownRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const itemRefs = useRef<Array<HTMLDivElement | null>>([]);
  const targetScrollIndexRef = useRef<number>(0);
  const scrolledOnOpenRef = useRef<boolean>(false);

  const TYPEAHEAD_RESET_MS = 1000;
  const typeBufferRef = useRef('');
  const bufferTimerRef = useRef<number | null>(null);
  const lastGoodBufferRef = useRef('');
  const lastGoodIndexRef = useRef<number | null>(null);
  const selectedLabel = useMemo(
    () => data.find((item) => item.value === value)?.label,
    [data, value]
  );

  useEffect(() => {
    if (!hasOptions) {
      setActiveIndex(-1);
      return;
    }
    const idx = data.findIndex((d) => d.value === value);
    if (idx >= 0) setActiveIndex(idx);
  }, [value, data, hasOptions]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const norm = (s: string) =>
    s
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '');

  const extractPhoneParts = (label: string): { dial?: string; iso?: string } => {
    const m = label.match(/^\s*\+?(\d+)\s*\(\s*([A-Z]{2})\s*\)/);
    if (!m) return {};
    return { dial: m[1], iso: m[2] };
  };

  const regionNames = useMemo(() => {
    const Ctor = (Intl as any).DisplayNames;
    if (typeof Ctor !== 'function') return null;
    const locale = (typeof navigator !== 'undefined' && (navigator as any)?.language)
      ? [ (navigator as any).language ]
      : ['en'];
    try {
      return new Ctor(locale, { type: 'region' });
    } catch (e) {
      try {
        return new Ctor(['en'], { type: 'region' });
      } catch (e2) {
        return null;
      }
    }
  }, []);

  const getCountryName = (iso?: string) => {
    if (!iso || !regionNames) return undefined;
    try {
      return regionNames.of(iso);
    } catch {
      return undefined;
    }
  };

  const getSearchKeys = (label: string) => {
    const { dial, iso } = extractPhoneParts(label);
    const name = getCountryName(iso);
    return {
      iso: iso ? norm(iso) : '',
      name: name ? norm(name) : '',
      full: norm(label),
      dial: dial ? norm('+' + dial) : '',
    };
  };

  const matchIndexFor = (options: Option[], query: string, startAt = 0) => {
    if (!query || options.length === 0) return -1;
    const q = norm(query);
    const n = options.length;

    const pass = (predicate: (idx: number) => boolean) => {
      for (let i = 0; i < n; i++) {
        const idx = (startAt + i) % n;
        if (predicate(idx)) return idx;
      }
      return -1;
    };

    let res = pass((idx) => getSearchKeys(options[idx].label).iso.startsWith(q));
    if (res >= 0) return res;

    res = pass((idx) => getSearchKeys(options[idx].label).name.startsWith(q));
    if (res >= 0) return res;

    res = pass((idx) => getSearchKeys(options[idx].label).full.startsWith(q));
    if (res >= 0) return res;

    res = pass((idx) => getSearchKeys(options[idx].label).full.includes(q));
    return res;
  };

  const scrollIntoViewIndex = (index: number) => {
    if (index < 0) return;
    const el = itemRefs.current[index];
    if (!el || !listRef.current) return;
    el.scrollIntoView({ block: 'nearest' });
  };

  const computeTargetIndex = () => {
    if (!hasOptions) return 0;
    if (openScroll === 'top') return 0;
    const selectedIdx = data.findIndex((d) => d.value === value);
    if (openScroll === 'selected') return selectedIdx >= 0 ? selectedIdx : 0;
    if (activeIndex >= 0) return activeIndex;
    return selectedIdx >= 0 ? selectedIdx : 0;
  };

  const prepareOpenTarget = () => {
    targetScrollIndexRef.current = computeTargetIndex();
    scrolledOnOpenRef.current = false;
  };

  const openMenu = () => {
    if (!isOpen) {
      prepareOpenTarget();
      setIsOpen(true);
    }
  };

  const openAndFocus = () => {
    openMenu();
    requestAnimationFrame(() => scrollIntoViewIndex(activeIndex));
  };

  const resetTypeaheadSoon = () => {
    if (bufferTimerRef.current) window.clearTimeout(bufferTimerRef.current);
    bufferTimerRef.current = window.setTimeout(() => {
      typeBufferRef.current = '';
      lastGoodBufferRef.current = '';
      lastGoodIndexRef.current = null;
    }, TYPEAHEAD_RESET_MS);
  };

  useEffect(() => {
    if (!isOpen) {
      targetScrollIndexRef.current = computeTargetIndex();
    }
  }, [data, value, activeIndex, isOpen, openScroll]);

  const onKeyDown = (e: React.KeyboardEvent<HTMLButtonElement>) => {
    if (disabled || !hasOptions) return;
    const { key } = e;

    if (key === 'ArrowDown') {
      e.preventDefault();
      openAndFocus();
      setActiveIndex((idx) => {
        const base = idx < 0 ? 0 : idx;
        const next = Math.min(data.length - 1, base + 1);
        requestAnimationFrame(() => scrollIntoViewIndex(next));
        lastGoodIndexRef.current = next;
        lastGoodBufferRef.current = typeBufferRef.current;
        return next;
      });
      return;
    }
    if (key === 'ArrowUp') {
      e.preventDefault();
      openAndFocus();
      setActiveIndex((idx) => {
        const base = idx < 0 ? 0 : idx;
        const prev = Math.max(0, base - 1);
        requestAnimationFrame(() => scrollIntoViewIndex(prev));
        lastGoodIndexRef.current = prev;
        lastGoodBufferRef.current = typeBufferRef.current;
        return prev;
      });
      return;
    }
    if (key === 'Enter') {
      e.preventDefault();
      if (!isOpen) {
        openMenu();
      } else {
        const chosen = data[activeIndex];
        if (chosen) onChange(chosen.value);
        setIsOpen(false);
      }
      return;
    }
    if (key === 'Escape') {
      e.preventDefault();
      setIsOpen(false);
      return;
    }

    const isPrintable = key.length === 1 && !e.ctrlKey && !e.metaKey && !e.altKey;

    if (key === ' ' && !isOpen) {
      e.preventDefault();
      openMenu();
      return;
    }

    if (isPrintable) {
      e.preventDefault();
      if (bufferTimerRef.current) window.clearTimeout(bufferTimerRef.current);

      const prevBuffer = typeBufferRef.current;
      const newBuffer = prevBuffer + key;

      openAndFocus();

      const startFrom = activeIndex >= 0 ? activeIndex : 0;
      const idx = matchIndexFor(data, newBuffer, startFrom);

      if (idx >= 0) {
        typeBufferRef.current = newBuffer;
        lastGoodBufferRef.current = newBuffer;
        lastGoodIndexRef.current = idx;
        setActiveIndex(idx);
        requestAnimationFrame(() => scrollIntoViewIndex(idx));
      } else {
        if (lastGoodIndexRef.current != null) {
          setActiveIndex(lastGoodIndexRef.current);
          requestAnimationFrame(() =>
            scrollIntoViewIndex(lastGoodIndexRef.current as number)
          );
        }
        typeBufferRef.current = lastGoodBufferRef.current;
      }

      resetTypeaheadSoon();
      return;
    }

    if (key === 'Backspace') {
      if (typeBufferRef.current.length > 0) {
        e.preventDefault();
        if (bufferTimerRef.current) window.clearTimeout(bufferTimerRef.current);

        const newBuffer = typeBufferRef.current.slice(0, -1);
        const startFrom = activeIndex >= 0 ? activeIndex : 0;
        const idx = matchIndexFor(data, newBuffer, startFrom);
        typeBufferRef.current = newBuffer;

        if (idx >= 0) {
          lastGoodBufferRef.current = newBuffer;
          lastGoodIndexRef.current = idx;
          setActiveIndex(idx);
          requestAnimationFrame(() => scrollIntoViewIndex(idx));
        } else {
          if (newBuffer === '') {
            lastGoodBufferRef.current = '';
            lastGoodIndexRef.current = null;
          }
        }

        resetTypeaheadSoon();
      }
    }
  };

  return (
    <div ref={dropdownRef} className={`relative w-full ${className}`}>
      <button
        type="button"
        onClick={() => {
          if (!disabled) {
            setIsOpen((prev) => {
              const next = !prev;
              if (next) prepareOpenTarget();
              return next;
            });
          }
        }}
        onKeyDown={onKeyDown}
        className={`w-full flex items-center justify-between px-3 py-[6.75px] border-[1.5px] rounded-lg text-sm
        transition focus:outline-none focus:ring-1
          ${
            disabled
              ? 'bg-gray-100 text-muted-foreground border-primary cursor-not-allowed'
              : error
                ? 'border-red-500 focus:ring-red-500'
                : isOpen
                  ? 'ring-1 ring-primary border-primary'
                  : 'bg-white border-secondary hover:border-primary'
          }
        `}
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        aria-controls="custom-select-listbox"
      >
        <span className={value ? 'text-black' : 'text-muted-foreground'}>
          {selectedLabel || placeholder}
        </span>
        <ChevronDown className="w-4 h-4 text-muted-foreground" />
      </button>

      {isOpen && (
        <div
          id="custom-select-listbox"
          ref={listRef}
          role="listbox"
          className="absolute z-10 w-full mt-1 overflow-y-auto bg-white border rounded-lg shadow-md max-h-60"
        >
          {hasOptions ? (
            data.map((item, idx) => {
              const isSelected = item.value === value;
              const isActive = idx === activeIndex;
              return (
                <div
                  key={item.value}
                  ref={(el) => {
                    itemRefs.current[idx] = el;
                    if (
                      el &&
                      !scrolledOnOpenRef.current &&
                      idx === targetScrollIndexRef.current
                    ) {
                      el.scrollIntoView({ block: 'nearest' });
                      scrolledOnOpenRef.current = true;
                    }
                  }}
                  role="option"
                  aria-selected={isSelected}
                  onMouseEnter={() => setActiveIndex(idx)}
                  onClick={() => {
                    onChange(item.value);
                    setIsOpen(false);
                  }}
                  className={[
                    'px-3 py-2 text-sm cursor-pointer transition',
                    isActive
                      ? 'bg-secondary text-white'
                      : isSelected
                        ? 'bg-primary text-white'
                        : 'hover:bg-secondary hover:text-white',
                  ].join(' ')}
                >
                  {item.label}
                </div>
              );
            })
          ) : (
            <div className="px-3 py-2 text-sm text-muted-foreground">No options</div>
          )}
        </div>
      )}

      {error && errorMessage && <p className="mt-1 text-sm text-red-500">{errorMessage}</p>}
    </div>
  );
};
